import AddIcon from '@mui/icons-material/Add'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { Autocomplete, TextField } from '@mui/material'
import React from 'react'
import { clip } from '../utils/inputs'
import type { ValueRow } from '../utils/value-rows'

export interface DescriptionValueListProps {
    title: string
    hint?: string
    rows: ValueRow[]
    descripcionLabel: string
    valorLabel: string
    descripcionMax: number
    valorMax: number
    /** Sugerencias para la descripción; se permite texto libre. */
    suggestions?: string[]
    addLabel: string
    emptyText: string
    onAdd: () => void
    onChange: (key: string, patch: Partial<Pick<ValueRow, 'descripcion' | 'valor'>>) => void
    onRemove: (key: string) => void
    onMove: (key: string, delta: -1 | 1) => void
}

const iconBtnClass = 'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border border-k-line bg-k-surface text-k-ink-2 hover:bg-k-hover disabled:cursor-not-allowed disabled:opacity-40'

const DescriptionValueList = ({
    title,
    hint,
    rows,
    descripcionLabel,
    valorLabel,
    descripcionMax,
    valorMax,
    suggestions,
    addLabel,
    emptyText,
    onAdd,
    onChange,
    onRemove,
    onMove,
}: DescriptionValueListProps) => (
    <div className="flex flex-col gap-3 rounded-2xl border border-k-line bg-k-bg px-4 py-3.5">
        <div className="flex flex-col gap-0.5">
            <span className="text-sm font-extrabold text-k-ink">{title} <span className="font-semibold text-k-muted">({rows.length})</span></span>
            {hint && <span className="text-xs text-k-muted">{hint}</span>}
        </div>

        {rows.length === 0 && <p className="m-0 text-sm italic text-k-muted">{emptyText}</p>}

        {rows.map((row, index) => (
            <div key={row.key} className="grid grid-cols-1 items-start gap-2.5 rounded-xl border border-k-line bg-k-surface p-3 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto]">
                {suggestions ? (
                    <Autocomplete
                        freeSolo
                        disableClearable
                        options={suggestions}
                        inputValue={row.descripcion}
                        onInputChange={(_, value) => onChange(row.key, { descripcion: clip(value, descripcionMax) })}
                        slotProps={{ paper: { className: 'v2-select-menu' } }}
                        renderInput={(params) => (
                            <TextField {...params} className="v2-field" variant="filled" fullWidth label={descripcionLabel} />
                        )}
                    />
                ) : (
                    <TextField
                        className="v2-field" variant="filled" fullWidth
                        label={descripcionLabel}
                        value={row.descripcion}
                        onChange={(event) => onChange(row.key, { descripcion: clip(event.target.value, descripcionMax) })}
                    />
                )}
                <TextField
                    className="v2-field" variant="filled" fullWidth
                    label={valorLabel}
                    value={row.valor}
                    onChange={(event) => onChange(row.key, { valor: clip(event.target.value, valorMax) })}
                />
                <div className="flex items-center gap-1.5 md:pt-[calc(15px*1.25+8px)]">
                    <button type="button" aria-label="Subir" title="Subir" className={iconBtnClass} disabled={index === 0} onClick={() => onMove(row.key, -1)}>
                        <KeyboardArrowUpIcon style={{ fontSize: 18 }} />
                    </button>
                    <button type="button" aria-label="Bajar" title="Bajar" className={iconBtnClass} disabled={index === rows.length - 1} onClick={() => onMove(row.key, 1)}>
                        <KeyboardArrowDownIcon style={{ fontSize: 18 }} />
                    </button>
                    <button type="button" aria-label="Eliminar" title="Eliminar" className={`${iconBtnClass} text-k-danger`} onClick={() => onRemove(row.key)}>
                        <DeleteOutlinedIcon style={{ fontSize: 16 }} />
                    </button>
                </div>
            </div>
        ))}

        <button
            type="button"
            className="inline-flex h-10 items-center justify-center gap-1.5 self-start rounded-xl border border-dashed border-k-line-strong bg-transparent px-3.5 text-[13px] font-bold text-k-ink hover:bg-k-hover"
            onClick={onAdd}
        >
            <AddIcon style={{ fontSize: 16 }} />
            {addLabel}
        </button>
    </div>
)

export default DescriptionValueList
